/**
 * Route registry.
 *
 * The sidebar, the command palette's navigation group and the router's protected
 * routes all read this one array, so a new screen is added in exactly one place and
 * cannot appear in the menu while being unreachable (or vice versa).
 *
 * `permission` is checked against the same shared role matrix the API enforces, so a
 * viewer never sees a link that would 403.
 */

import {
  Building2,
  CalendarClock,
  BellRing,
  ChartNoAxesCombined,
  FileSpreadsheet,
  FileText,
  LayoutDashboard,
  type LucideIcon,
  Receipt,
  RefreshCw,
  ScrollText,
  Settings,
  ShieldAlert,
  Users,
  UsersRound,
} from 'lucide-react';
import type { Permission } from '@ias/shared';

import type { TranslationKey } from '@/lib/i18n/dictionaries';

export interface NavItem {
  to: string;
  labelKey: TranslationKey;
  icon: LucideIcon;
  permission?: Permission;
  /** Badge source, resolved by the sidebar (e.g. count of overdue reminders). */
  badge?: 'renewals' | 'overdueReminders';
  /**
   * How loudly the badge reads. Counts default to quiet: "38 renewals this month" is
   * information, and a sidebar where every number is red teaches people to ignore red.
   * Only a genuinely overdue queue is allowed to alarm.
   */
  badgeTone?: 'quiet' | 'alarm';
  /** Matches child routes so a detail page keeps its parent nav item active. */
  matchPrefix?: boolean;
}

export interface NavGroup {
  labelKey: TranslationKey;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    labelKey: 'nav.groupMain',
    items: [
      // D11 review outcome (2026-09-07): the first view is the dashboard — general
      // information from the whole system. The task-list experiment («Η μέρα μου») was
      // judged too directive; its win-back strip, collections position and expiring
      // documents merged into the dashboard, and the per-task worklists (Ανανεώσεις,
      // Εισπράξεις, Υπενθυμίσεις) each have their own screen below.
      { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { to: '/clients', labelKey: 'nav.clients', icon: Users, permission: 'client:read', matchPrefix: true },
      { to: '/policies', labelKey: 'nav.policies', icon: FileText, permission: 'policy:read', matchPrefix: true },
      { to: '/renewals', labelKey: 'nav.renewals', icon: RefreshCw, permission: 'policy:read', badge: 'renewals' },
      /*
       * Ειδοποιητήρια replaces Εισπράξεις here. The collections worklist chased premium
       * instalments, which in this office are settled between the client and their bank — the
       * agency never touches them, so the screen listed work nobody does. The call before a
       * renewal is the work, and it had no screen at all.
       */
      { to: '/notices', labelKey: 'nav.notices', icon: BellRing, permission: 'policy:read' },
      { to: '/claims', labelKey: 'nav.claims', icon: ShieldAlert, permission: 'claim:read', matchPrefix: true },
      {
        to: '/reminders',
        labelKey: 'nav.reminders',
        icon: CalendarClock,
        permission: 'reminder:read',
        badge: 'overdueReminders',
        badgeTone: 'alarm',
      },
    ],
  },
  {
    labelKey: 'nav.groupInsight',
    items: [
      { to: '/commissions', labelKey: 'nav.commissions', icon: Receipt, permission: 'commission:read' },
      { to: '/reports', labelKey: 'nav.reports', icon: ChartNoAxesCombined, permission: 'report:read' },
    ],
  },
  {
    labelKey: 'nav.groupAdmin',
    items: [
      { to: '/insurers', labelKey: 'nav.companies', icon: Building2, permission: 'policy:read' },
      { to: '/users', labelKey: 'nav.users', icon: UsersRound, permission: 'user:manage' },
      { to: '/import', labelKey: 'nav.import', icon: FileSpreadsheet, permission: 'import:run' },
      { to: '/audit', labelKey: 'nav.audit', icon: ScrollText, permission: 'audit:read' },
      { to: '/settings', labelKey: 'nav.settings', icon: Settings },
    ],
  },
];

/** Flat list, used by the command palette. */
export const NAV_ITEMS: NavItem[] = NAVIGATION.flatMap((group) => group.items);

/** Route for a search hit or a reminder's related entity. */
export function entityRoute(entity: string, id: string): string {
  const routes: Record<string, string> = {
    client: '/clients',
    policy: '/policies',
    claim: '/claims',
  };
  return `${routes[entity] ?? '/'}/${id}`;
}
